import React, { useState, useEffect, useMemo } from "react";
import { TileCard, EmpButton as Button } from '@empowernow/ui';
import { message } from 'antd';
import { executeGraphWorkflow } from '../core/services/workflowExecution';
import { DataFrameFieldRenderer } from './DataFrameFieldRenderer';
import { MatrixFieldRenderer } from './MatrixFieldRenderer';
import { FormField, FormDefinition, FormPreviewProps } from './types';

// Theme-aware style constants
const styles = {
  label: { color: 'var(--main-text)' },
  required: { color: 'var(--error-color)' },
  error: { color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' },
  input: {
    normal: {
      background: 'var(--input-bg)',
      border: '1px solid var(--input-border)',
    },
    readonly: {
      background: 'var(--disabled-bg)',
      border: '1px solid var(--input-border)',
    },
    error: {
      background: 'var(--input-bg)',
      border: '1px solid var(--error-border)',
    }
  },
  infoBox: {
    background: 'var(--tag-bg)',
    border: '1px solid var(--tag-border)',
    color: 'var(--main-text)',
  },
  errorBanner: {
    background: 'var(--error-bg)',
    border: '1px solid var(--error-border)',
    color: 'var(--error-color)',
  },
  divider: {
    borderTop: '1px solid var(--divider-color)'
  },
  button: {
    secondary: {
      background: 'var(--input-bg)',
      border: '1px solid var(--input-border)',
      color: 'var(--main-text-secondary)',
    }
  }
};

export function FormPreview({
  form,
  initialValues = {},
  onSubmit,
  onCancel,
  readOnly = false,
}: FormPreviewProps) {
  // Memoize the default initialValues to ensure a stable reference
  const memoizedInitialValues = useMemo(() => initialValues, [initialValues]);
  
  const [formData, setFormData] = useState<Record<string, any>>(memoizedInitialValues);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    console.log('FormPreview - updating formData with initialValues:', memoizedInitialValues);
    setFormData(memoizedInitialValues);
  }, [memoizedInitialValues]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + Enter = Submit
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !readOnly && !isSubmitting) {
        e.preventDefault();
        handleSubmit(e as any);
      }
      // Escape = Cancel
      if (e.key === 'Escape' && onCancel) {
        e.preventDefault();
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSubmitting, readOnly, formData]);

  const handleInputChange = (fieldName: string, value: any) => {
    if (readOnly) return;
    
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const validateField = (field: FormField, value: any): string | null => {
    const fieldLabel = field.label || field.name;
    
    // Smart required message based on field type
    if (field.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
      const smartRequiredMessages: Record<string, string> = {
        'email': `We need an email address to contact ${fieldLabel.toLowerCase().includes('manager') ? 'the manager' : 'this user'}`,
        'password': 'Choose a secure password to protect the account',
        'text': fieldLabel.toLowerCase().includes('name') 
          ? `${fieldLabel} is required to identify this ${form.title?.toLowerCase().includes('user') ? 'user' : 'entry'}`
          : `${fieldLabel} is required`,
        'select': `Please select ${fieldLabel.toLowerCase()}`,
        'number': `${fieldLabel} value is required`,
      };
      return smartRequiredMessages[field.type] || `${fieldLabel} is required`;
    }

    if (field.validation) {
      const { min, max, pattern, message } = field.validation;
      
      if (min !== undefined && typeof value === 'string' && value.length < min) {
        const remaining = min - value.length;
        return message || `Add ${remaining} more character${remaining !== 1 ? 's' : ''} (minimum ${min})`;
      }
      
      if (max !== undefined && typeof value === 'string' && value.length > max) {
        const excess = value.length - max;
        return message || `Remove ${excess} character${excess !== 1 ? 's' : ''} (maximum ${max})`;
      }
      
      if (pattern && typeof value === 'string' && !new RegExp(pattern).test(value)) {
        // Smart pattern messages
        if (field.type === 'email') {
          return message || 'Please enter a valid email address (e.g., user@company.com)';
        }
        if (field.name.toLowerCase().includes('password')) {
          return message || 'Password must include uppercase, lowercase, number, and special character';
        }
        return message || `Invalid format for ${fieldLabel}`;
      }
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (readOnly) return;
    
    // Validate all fields
    const newErrors: Record<string, string> = {};
    form.fields.forEach(field => {
      const error = validateField(field, formData[field.name]);
      if (error) {
        newErrors[field.name] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else if (form.workflowName) {
        // Default submission behavior - same as visual_designer
        await executeGraphWorkflow(form.workflowName, formData);
        message.success("Form submitted successfully");
      } else {
        // If no onSubmit and no workflowName, just show success
        message.success("Form submitted successfully");
      }

      // Reset form after successful submission - same as visual_designer
      setFormData({});
      setErrors({});
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Form submission failed";
      setErrors({
        ...errors,
        "_form": errorMessage
      });
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const value = formData[field.name] || '';
    const error = errors[field.name];
    const isFieldReadOnly = readOnly || field.readOnly || form.isViewOnly;

    switch (field.type) {
      case 'text':
      case 'email':
      case 'number':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type={field.type}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'text',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onFocus={(e) => {
                if (!isFieldReadOnly) {
                  e.target.style.borderColor = 'var(--main-accent)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                  e.target.style.transform = 'translateY(-1px)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = error ? 'var(--error-color)' : 'var(--border-color)';
                e.target.style.boxShadow = 'none';
                e.target.style.transform = 'translateY(0)';
              }}
            />
            {error && (
              <div style={{ 
                color: 'var(--error-color)', 
                fontSize: '12px', 
                marginTop: '4px',
                animation: 'slideIn 0.2s ease-out'
              }}>
                {error}
              </div>
            )}
          </div>
        );
      
      case 'password':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={(field as any).visibilityToggle && (formData as any)[`_show_${field.name}`] ? 'text' : 'password'}
                value={value}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                disabled={isFieldReadOnly}
                style={{
                  width: '100%',
                  background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                  border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  paddingRight: (field as any).visibilityToggle ? '40px' : '12px',
                  color: 'var(--main-text)',
                  fontSize: '14px',
                  outline: 'none',
                  cursor: isFieldReadOnly ? 'not-allowed' : 'text',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
                onFocus={(e) => {
                  if (!isFieldReadOnly) {
                    e.target.style.borderColor = 'var(--main-accent)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = error ? 'var(--error-color)' : 'var(--border-color)';
                  e.target.style.boxShadow = 'none';
                  e.target.style.transform = 'translateY(0)';
                }}
              />
              {(field as any).visibilityToggle && !isFieldReadOnly && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      [`_show_${field.name}`]: !(prev as any)[`_show_${field.name}`]
                    }));
                  }}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--main-text-secondary)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.7,
                    transition: 'opacity 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                >
                  {(formData as any)[`_show_${field.name}`] ? '👁️' : '👁️‍🗨️'}
                </button>
              )}
            </div>
            {error && (
              <div style={{ 
                color: 'var(--error-color)', 
                fontSize: '12px', 
                marginTop: '4px',
                animation: 'slideIn 0.2s ease-out'
              }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <textarea
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              disabled={isFieldReadOnly}
              rows={4}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                resize: 'vertical',
                cursor: isFieldReadOnly ? 'not-allowed' : 'text',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onFocus={(e) => {
                if (!isFieldReadOnly) {
                  e.target.style.borderColor = 'var(--main-accent)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = error ? 'var(--error-color)' : 'var(--border-color)';
                e.target.style.boxShadow = 'none';
              }}
            />
            {error && (
              <div style={{ 
                color: 'var(--error-color)', 
                fontSize: '12px', 
                marginTop: '4px',
                animation: 'slideIn 0.2s ease-out'
              }}>
                {error}
              </div>
            )}
          </div>
        );
      
      case 'section':
        // Section divider (visual separator, not a data field)
        return (
          <div key={field.name} style={{ 
            marginTop: '32px',
            marginBottom: '24px',
            paddingTop: '24px',
            borderTop: '2px solid var(--border-color)'
          }}>
            <h4 style={{
              color: 'var(--main-text)',
              fontSize: '16px',
              fontWeight: 600,
              margin: 0,
              marginBottom: '8px'
            }}>
              {field.label || field.name}
            </h4>
            {field.placeholder && (
              <p style={{
                color: 'var(--main-text-secondary)',
                fontSize: '13px',
                margin: 0
              }}>
                {field.placeholder}
              </p>
            )}
          </div>
        );

      case 'select':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <select
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              disabled={isFieldReadOnly}
              className="form-select-enhanced"
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              onFocus={(e) => {
                if (!isFieldReadOnly) {
                  e.target.style.borderColor = 'var(--main-accent)';
                  e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                }
              }}
              onBlur={(e) => {
                e.target.style.borderColor = error ? 'var(--error-color)' : 'var(--border-color)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="" style={{ color: 'var(--main-text-secondary)' }}>
                Select {field.label || field.name}
              </option>
              {field.options?.map((option) => (
                <option 
                  key={option.value} 
                  value={option.value}
                  style={{
                    color: 'var(--main-text)',
                    background: 'var(--dropdown-bg)',
                    padding: '8px'
                  }}
                >
                  {option.label}
                </option>
              ))}
            </select>
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: 'var(--main-text)',
              fontSize: '14px',
              cursor: isFieldReadOnly ? 'not-allowed' : 'pointer'
            }}>
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => handleInputChange(field.name, e.target.checked)}
                disabled={isFieldReadOnly}
                style={{ marginRight: '8px' }}
              />
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            {field.options?.map((option) => (
              <label key={option.value} style={{
                display: 'flex',
                alignItems: 'center',
                color: 'var(--main-text)',
                fontSize: '14px',
                marginBottom: '8px',
                cursor: isFieldReadOnly ? 'not-allowed' : 'pointer'
              }}>
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  disabled={isFieldReadOnly}
                  style={{ marginRight: '8px' }}
                />
                {option.label}
              </label>
            ))}
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type="date"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'text'
              }}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'time':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type="time"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'text'
              }}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'phone':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type="tel"
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder || "Enter phone number"}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'text'
              }}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'file':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type="file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                handleInputChange(field.name, file);
              }}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'pointer'
              }}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'plaintext':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
            </label>
            <div style={{
              background: 'var(--tag-bg)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              padding: '8px 12px',
              color: 'var(--main-text)',
              fontSize: '14px'
            }}>
              {value || field.placeholder || 'Static text content'}
            </div>
          </div>
        );

      case 'range':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}: {value || field.validation?.min || 0}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type="range"
              min={field.validation?.min || 0}
              max={field.validation?.max || 100}
              value={value || field.validation?.min || 0}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: 'var(--dropdown-bg)',
                cursor: isFieldReadOnly ? 'not-allowed' : 'pointer'
              }}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'autocomplete':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '4px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <input
              type="text"
              list={`${field.name}-datalist`}
              value={value}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              disabled={isFieldReadOnly}
              style={{
                width: '100%',
                background: isFieldReadOnly ? 'var(--disabled-bg)' : 'var(--dropdown-bg)',
                border: error ? '1px solid var(--error-color)' : '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: '8px 12px',
                color: 'var(--main-text)',
                fontSize: '14px',
                outline: 'none',
                cursor: isFieldReadOnly ? 'not-allowed' : 'text'
              }}
            />
            <datalist id={`${field.name}-datalist`}>
              {field.options?.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </datalist>
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'dataframe':
        // Resolve parameter dependencies from form data
        const resolvedParams: Record<string, any> = {};
        if (field.dataSource?.params) {
          Object.entries(field.dataSource.params).forEach(([key, param]) => {
            if (typeof param === 'object' && param !== null && 'sourceField' in param) {
              // Resolve from form data
              const sourceValue = formData[(param as any).sourceField];
              if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
                let transformedValue = sourceValue;
                // Apply transformation if specified
                if ((param as any).transform === 'toString') {
                  transformedValue = String(sourceValue);
                } else if ((param as any).transform === 'toNumber') {
                  transformedValue = Number(sourceValue);
                }
                resolvedParams[key] = transformedValue;
              }
            } else if (typeof param === 'object' && param !== null && 'value' in param) {
              // Handle { value: "100", transform: "toString" } format
              let paramValue = (param as any).value;
              if ((param as any).transform === 'toString') {
                paramValue = String(paramValue);
              } else if ((param as any).transform === 'toNumber') {
                paramValue = Number(paramValue);
              }
              resolvedParams[key] = paramValue;
            } else {
              // Static value
              resolvedParams[key] = param;
            }
          });
        }
        
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <DataFrameFieldRenderer
              field={field}
              value={value}
              onChange={(newValue) => handleInputChange(field.name, newValue)}
              dependencyParams={resolvedParams}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      case 'matrix':
        return (
          <div key={field.name} style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              color: 'var(--main-text)',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '8px'
            }}>
              {field.label || field.name}
              {field.required && <span style={{ color: 'var(--error-color)' }}> *</span>}
            </label>
            <MatrixFieldRenderer
              field={field}
              value={value || []}
              onChange={(newValue) => handleInputChange(field.name, newValue)}
            />
            {error && (
              <div style={{ color: 'var(--error-color)', fontSize: '12px', marginTop: '4px' }}>
                {error}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  // Check if form has sections defined
  const hasSections = (form as any).sections && Array.isArray((form as any).sections);
  
  // Initialize collapsed sections from form definition
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    if (!hasSections) return new Set();
    const collapsed = new Set<string>();
    (form as any).sections.forEach((section: any) => {
      if (section.collapsed) {
        collapsed.add(section.id);
      }
    });
    return collapsed;
  });
  
  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  return (
    <div style={{ padding: '0' }}>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .form-select-enhanced option {
          color: var(--main-text) !important;
          background: var(--dropdown-bg) !important;
        }
      `}</style>
      
      {/* Form Header - WOW Edition */}
      {(form.title || form.description) && (
        <div style={{ 
          marginBottom: '28px',
          paddingBottom: '20px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          {form.title && (
            <h3 style={{ 
              color: 'var(--main-text)', 
              margin: 0, 
              marginBottom: form.description ? '12px' : 0,
              fontSize: '22px',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.85) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {form.title}
            </h3>
          )}
          {form.description && (
            <p style={{ 
              color: 'rgba(255,255,255,0.65)',
              fontSize: '13px',
              margin: 0,
              lineHeight: '1.5',
              maxWidth: '90%',
            }}>
              {form.description}
            </p>
          )}
          
          {/* User context banner (if editing existing user) */}
          {initialValues?.SystemIdentifier && (initialValues?.FirstName || initialValues?.Email) && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(59,130,246,0.05))',
              border: '1px solid rgba(59,130,246,0.3)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <span style={{ 
                fontSize: '28px',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
              }}>
                👤
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ 
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: '15px',
                  fontWeight: 600,
                  marginBottom: '2px',
                }}>
                  {initialValues?.FirstName && initialValues?.LastName 
                    ? `${initialValues.FirstName} ${initialValues.LastName}`
                    : initialValues?.DisplayName || 'User'}
                </div>
                {initialValues?.Email && (
                  <div style={{ 
                    color: 'rgba(255,255,255,0.65)',
                    fontSize: '13px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {initialValues.Email}
                  </div>
                )}
              </div>
              {initialValues?.JobTitle && (
                <div style={{
                  padding: '4px 10px',
                  background: 'rgba(59,130,246,0.2)',
                  border: '1px solid rgba(59,130,246,0.4)',
                  borderRadius: '12px',
                  fontSize: '12px',
                  color: 'rgba(59,130,246,0.95)',
                  fontWeight: 500,
                }}>
                  {initialValues.JobTitle}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Form-level errors */}
      {errors["_form"] && (
        <div style={{
          background: 'var(--error-bg)',
          border: '1px solid var(--error-color)',
          borderRadius: '4px',
          padding: '12px',
          marginBottom: '16px',
          color: 'var(--error-color)',
          fontSize: '14px',
          animation: 'slideIn 0.3s ease-out'
        }}>
          {errors["_form"]}
        </div>
      )}

      {/* Form Fields */}
      <form onSubmit={handleSubmit}>
        {hasSections ? (
          /* Render fields grouped by sections */
          (form as any).sections.map((section: any) => {
            const sectionFields = form.fields.filter((f: FormField) => 
              section.fields?.includes(f.name)
            );
            const isCollapsed = collapsedSections.has(section.id);
            
            return (
              <div key={section.id} style={{ marginBottom: '24px' }}>
                <button
                  type="button"
                  onClick={() => toggleSection(section.id)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'var(--card-header-bg)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    padding: '12px 16px',
                    color: 'var(--main-text)',
                    fontSize: '16px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    marginBottom: '12px',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--card-hover-bg)';
                    e.currentTarget.style.borderColor = 'var(--main-accent)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--card-header-bg)';
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  <span>{section.title}</span>
                  <span style={{ 
                    transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                  }}>
                    ▼
                  </span>
                </button>
                {!isCollapsed && (
                  <div style={{
                    animation: 'slideIn 0.3s ease-out',
                    padding: '0 8px'
                  }}>
                    {section.description && (
                      <p style={{
                        color: 'var(--main-text-secondary)',
                        fontSize: '13px',
                        marginBottom: '16px',
                        marginTop: '-4px'
                      }}>
                        {section.description}
                      </p>
                    )}
                    {sectionFields.map(renderField)}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          /* Render fields without sections */
          form.fields.map(renderField)
        )}
        
        {/* Keyboard Shortcuts Hint */}
        {!readOnly && !form.isViewOnly && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginTop: '16px',
            paddingTop: '12px',
            fontSize: '12px',
            color: 'var(--main-text-secondary)',
            opacity: 0.7
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <kbd style={{
                background: 'var(--tag-bg)',
                border: '1px solid var(--tag-border)',
                borderRadius: '3px',
                padding: '2px 6px',
                fontSize: '11px',
                fontFamily: 'monospace'
              }}>⌘ Enter</kbd>
              <span>to submit</span>
            </span>
            {onCancel && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <kbd style={{
                  background: 'var(--tag-bg)',
                  border: '1px solid var(--tag-border)',
                  borderRadius: '3px',
                  padding: '2px 6px',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}>Esc</kbd>
                <span>to cancel</span>
              </span>
            )}
          </div>
        )}
        
        {/* Submit and Cancel Buttons */}
        {!readOnly && !form.isViewOnly && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: '12px',
            marginTop: '16px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border-color)'
          }}>
            {onCancel && (
              <Button
                onClick={onCancel}
                style={{
                  background: 'var(--dropdown-bg)',
                  border: '1px solid var(--border-color)',
                  color: 'var(--main-text-secondary)',
                  padding: '8px 24px',
                  transition: 'all 0.2s',
                }}
              >
                Cancel
              </Button>
            )}
            {onSubmit && (
              <Button
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? 'var(--disabled-bg)' : 'var(--selected-bg)',
                  border: 'none',
                  color: 'var(--main-text)',
                  opacity: isSubmitting ? 0.7 : 1,
                  padding: '8px 24px',
                  transition: 'all 0.2s',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e as any);
                }}
              >
                {isSubmitting ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>⏳</span>
                    Submitting...
                  </span>
                ) : (
                  form.submitButtonText || 'Submit'
                )}
              </Button>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
